import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { SmartLinkDataGridField } from '~/common/types/SmartLinkDataGrid';
import { DataGridConfig, gridColDefFactory } from '~/components/DataGridCustom';

import { SmartLinkRow, SmartLinkRowFactory } from './SmatLinkGridForm.types';

export type UseSmartLinkDataGridConfigResult = {
  config: DataGridConfig<SmartLinkRow>;
  rowFactory: SmartLinkRowFactory;
};

export type UseSmartLinkDataGridConfigProps = {
  domain: string;
};

export type UseSmartLinkDataGridConfig = ({
  domain,
}: UseSmartLinkDataGridConfigProps) => UseSmartLinkDataGridConfigResult;

export const useSmartLinkDataGridConfig: UseSmartLinkDataGridConfig = ({ domain }) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'SmartLinksGrid.column' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });

    return {
      config: {
        columns: [
          createColumn({
            field: SmartLinkDataGridField.TITLE,
            minWidth: 192,
            hideable: false,
            resizable: false,
            editable: true,
            type: 'string',
          }),
          createColumn({
            field: SmartLinkDataGridField.DESCRIPTION,
            minWidth: 256,
            hideable: false,
            resizable: false,
            editable: true,
            type: 'string',
          }),
          createColumn({
            field: SmartLinkDataGridField.URL,
            minWidth: 192,
            hideable: false,
            resizable: false,
            editable: true,
            type: 'string',
          }),
          createColumn({
            field: SmartLinkDataGridField.HIDDEN,
            minWidth: 80,
            hideable: false,
            resizable: false,
            editable: true,
            type: 'boolean',
          }),
          createColumn({
            field: SmartLinkDataGridField.EXTERNAL,
            minWidth: 80,
            hideable: false,
            resizable: false,
            editable: true,
            type: 'boolean',
          }),
        ],
      },
      rowFactory: () => {
        return {
          id: uuidv4(),
          domain,
        };
      },
    } as UseSmartLinkDataGridConfigResult;
  }, [domain, t, tColumn]);
};
